const API_BASE = "https://api.userevaluation.com"
const GUEST_KEY = "ue_guest_session"
const SESSION_KEY = "ue_funnel_session"
const ATTRIBUTION_KEY = "ue_first_touch"

export type GuestMessage = {
  role: "user" | "assistant"
  text: string
  createdAt: number
  cards?: unknown[]
  sources?: GuestSource[]
}

export type GuestSource = {
  title: string
  url: string
  snippet?: string
  source?: string
}

export type GuestChatResponse = {
  message: string
  cards?: unknown[]
  followups?: string[]
  sources?: GuestSource[]
  wall?: { type: string } | null
  remaining?: number
  degraded?: boolean
}

type Attribution = {
  source: string
  referralSource: string
  landingPath: string
  referrer: string
  utm: Record<string, string>
}

type GuestSession = { guestId: string; guestToken: string }

declare global {
  interface Window {
    umami?: { track: (name: string, props?: Record<string, unknown>) => void }
  }
}

function randomId() {
  return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
}

function readJson<T>(storage: Storage, key: string): T | null {
  try {
    return JSON.parse(storage.getItem(key) || "null") as T | null
  } catch {
    return null
  }
}

export function captureAttribution(): Attribution {
  if (typeof window === "undefined") {
    return { source: "", referralSource: "", landingPath: "", referrer: "", utm: {} }
  }
  const stored = readJson<Attribution>(localStorage, ATTRIBUTION_KEY)
  if (stored) return stored

  const params = new URLSearchParams(window.location.search)
  const referralSource =
    params.get("referral") || params.get("ref") || params.get("r") || ""
  const utm = Object.fromEntries(
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]
      .map((key) => [key, params.get(key) || ""])
      .filter(([, value]) => value),
  )
  const attribution = {
    source:
      params.get("source") ||
      (utm.utm_source ? "campaign" : referralSource ? "referral" : "direct"),
    referralSource,
    landingPath: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || "",
    utm,
  }
  localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution))
  return attribution
}

function sessionId() {
  let id = sessionStorage.getItem(SESSION_KEY)
  if (!id) {
    id = randomId()
    sessionStorage.setItem(SESSION_KEY, id)
  }
  return id
}

export async function ensureGuestSession(): Promise<GuestSession> {
  const stored = readJson<GuestSession>(localStorage, GUEST_KEY)
  if (stored?.guestId && stored?.guestToken) return stored

  const guestId = randomId()
  const response = await fetch(`${API_BASE}/api/guest/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ guestId }),
  })
  if (!response.ok) throw new Error("Could not start a guest session")
  const session = (await response.json()) as GuestSession
  localStorage.setItem(GUEST_KEY, JSON.stringify(session))
  return session
}

export async function sendGuestMessage(message: string, history: GuestMessage[]) {
  const guest = await ensureGuestSession()
  const response = await fetch(`${API_BASE}/api/guest/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ guestToken: guest.guestToken, message, history }),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(data?.message || "The assistant could not answer")
  return data as GuestChatResponse
}

export async function streamGuestMessage(
  message: string,
  history: GuestMessage[],
  onDelta: (text: string) => void,
  onStatus?: (status: { tool?: string; label?: string }) => void,
) {
  const guest = await ensureGuestSession()
  const response = await fetch(`${API_BASE}/api/guest/chat/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ guestToken: guest.guestToken, message, history }),
  })

  if (response.status === 404) return sendGuestMessage(message, history)
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data?.message || "The assistant could not answer")
  }

  if (!response.body || !response.headers.get("content-type")?.includes("text/event-stream")) {
    return (await response.json()) as GuestChatResponse
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  let streamedMessage = ""
  let result: GuestChatResponse | null = null
  let streamError = ""

  const processFrame = (frame: string) => {
    let event = "message"
    const dataLines: string[] = []
    for (const line of frame.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim()
      if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart())
    }
    if (!dataLines.length) return

    const data = JSON.parse(dataLines.join("\n"))
    if (event === "delta" && typeof data?.text === "string") {
      streamedMessage += data.text
      onDelta(data.text)
    } else if (event === "status") {
      onStatus?.(data)
    } else if (event === "done") {
      result = data as GuestChatResponse
    } else if (event === "error") {
      streamError = String(data?.message || "The assistant could not finish this answer")
    }
  }

  while (true) {
    const { value, done } = await reader.read()
    buffer += decoder.decode(value, { stream: !done })
    buffer = buffer.replace(/\r\n/g, "\n")
    let boundary = buffer.indexOf("\n\n")
    while (boundary !== -1) {
      processFrame(buffer.slice(0, boundary))
      buffer = buffer.slice(boundary + 2)
      boundary = buffer.indexOf("\n\n")
    }
    if (done) break
  }
  if (buffer.trim()) processFrame(buffer)
  if (streamError) throw new Error(streamError)
  const completedResult = result as GuestChatResponse | null
  if (!completedResult) throw new Error("The assistant stream ended before completion")

  return {
    ...completedResult,
    message: completedResult.message || streamedMessage,
  }
}

export async function createGuestHandoff(messages: GuestMessage[]) {
  const guest = await ensureGuestSession()
  const response = await fetch(`${API_BASE}/api/guest/handoff`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      guestToken: guest.guestToken,
      messages,
      attribution: captureAttribution(),
    }),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok || !data?.handoffToken) {
    throw new Error(data?.message || "Could not continue this conversation")
  }
  return String(data.handoffToken)
}

export function trackFunnel(name: string, props: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return
  window.umami?.track(name, props)
  void ensureGuestSession()
    .then((guest) => {
      const attribution = captureAttribution()
      return fetch(`${API_BASE}/api/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          event: name,
          sessionId: sessionId(),
          guestId: guest.guestId,
          guestToken: guest.guestToken,
          path: `${window.location.pathname}${window.location.search}`,
          referrer: attribution.referrer,
          utm: attribution.utm,
          props,
        }),
      })
    })
    .catch(() => undefined)
}

export function attributedChatUrl(question: string) {
  const current = new URLSearchParams(window.location.search)
  const next = new URLSearchParams({ q: question, source: "homepage_chat" })
  for (const key of [
    "ref",
    "referral",
    "r",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ]) {
    const value = current.get(key)
    if (value) next.set(key, value)
  }
  return `/chat?${next.toString()}`
}
